import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useMonthlyStats } from "../hooks/useMonthlyStats";
import { useYearlyStats } from "../hooks/useYearlyStats";
import { useState } from "react";
import { TrendingUp, TrendingDown, DollarSign, Percent } from "lucide-react";

interface StatsSectionProps {
  formatCurrency: (amount: number) => string;
}

export const StatsSection = ({ formatCurrency }: StatsSectionProps) => {
  const [showYearly, setShowYearly] = useState(false);
  const { data: monthlyStats } = useMonthlyStats();
  const { data: yearlyStats } = useYearlyStats();
  
  const stats = showYearly ? yearlyStats : monthlyStats;
  const period = showYearly ? "l'année" : "du mois";

  const getPercentageColor = (value: number) => {
    if (value > 0) return "text-green-500";
    if (value < 0) return "text-red-500";
    return "text-gray-500";
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "revenue":
        return <DollarSign className="w-4 h-4 text-primary-500" />;
      case "expenses":
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case "profit":
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case "margin":
        return <Percent className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Aperçu {period}</h2>
        <div className="flex items-center space-x-2">
          <Label htmlFor="view-toggle">Vue annuelle</Label>
          <Switch
            id="view-toggle"
            checked={showYearly}
            onCheckedChange={setShowYearly}
          />
        </div>
      </div>

      {stats && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="bg-gradient-to-br from-primary-50 to-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Chiffre d'affaires
              </CardTitle>
              {getIcon("revenue")}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.revenue)}</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Dépenses
              </CardTitle>
              {getIcon("expenses")}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.expenses)}</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Bénéfice net
              </CardTitle>
              {getIcon("profit")}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.netProfit)}</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-white">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Marge bénéficiaire
              </CardTitle>
              {getIcon("margin")}
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${getPercentageColor(stats.profitMargin)}`}>
                {stats.profitMargin.toFixed(2)}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};